import "react-nestable/dist/styles/index.css";

import * as yup from "yup";

import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";
import {
  CREATE_USER,
  DELETE_USER,
  GET_BRANCH_USERS,
} from "../../../graphql/dodavanjekorisnika";
import Nestable, { Item } from "react-nestable";
import { encryptMessage, importKey } from "../../auth/cryptoUtils";
import { useEffect, useState } from "react";

import { ButtonMain } from "../../../components/Safe/SafeStyled";
import { DodavanjeKorisnikaResourcesBSInterface } from "./DodavanjeKorisnika.resources";
import { EnumUserType } from "../../../redux/interfaces";
import { FiTrash2 } from "react-icons/fi";
import { FormInput } from "../../../components";
import config from "../../../config";
import { useForm } from "react-hook-form";
import { useMutation } from "@apollo/client";
import { useQuery } from "@apollo/client";
import { useTypedResource } from "../../../i18n/languagehandler";
import { yupResolver } from "@hookform/resolvers/yup";

// Define the interface for the Adding User data
interface IUser {
  userId: number;
  createdAt: string;
  updatedAt: string;
  fullName: string;
  email: string;
  password: string;
  position: string;
  type: EnumUserType;
}

export interface IBranchUsersQuery {
  branchUsers?: IUser[];
}

interface IFormUser {
  email: string;
  fullName: string;
  password: string;
}

export interface IItem {
  position: string;
  users?: IFormUser[];
}

export interface IFormUsers {
  item: IItem[];
}

export interface IFormattedUsersJSON {
  Positions: IUserItemUser[];
}

export interface IUserItemUser {
  Id: number;
  Email: string;
  FullName: string;
  Password: string;
  Position: string;
  Type: EnumUserType;
  Active: boolean;
}

export interface ITextArea {
  users: string;
}

export interface RenderItemProps {
  item: Record<string, any>;
  collapseIcon: React.ReactNode;
  handler: React.ReactNode;
}

export interface FormattedItem {
  id: number;
  text: string;
  children?: FormattedItem[];
}

export interface ICreateUserData {
  email: string;
  password: string;
  fullName: string;
  position: string;
  type?: EnumUserType | null;
  branchIds?: number[] | null;
}

// Conversion function
export function convertArrayToJSON(arr: any[]): IFormattedUsersJSON {
  const convertedArray: IUserItemUser[] = arr.map((item) => ({
    Id: item.userId,
    Email: item.email,
    FullName: item.fullName,
    Password: item.password,
    Position: item.position,
    Type: item.type,
    Active: item.active,
  }));
  return { Positions: convertedArray };
}

export function formatUsersList(items: IUserItemUser[]): FormattedItem[] {
  const positionMap: Record<string, FormattedItem> = {};

  items.forEach((userItem: IUserItemUser) => {
    if (!positionMap[userItem.Position]) {
      positionMap[userItem.Position] = {
        id: userItem.Id,
        text: userItem.Position,
        children: [],
      };
    }

    const matchingUser = items.find(
      (user) => user.Position === userItem.Position,
    );

    if (matchingUser && userItem.Active) {
      const positionChildren = positionMap[userItem.Position].children;
      positionChildren &&
        positionChildren.push({
          id: userItem.Id,
          text: userItem.FullName + " -- " + userItem.Email,
          // You can include other user properties as needed
        });
    }
  });

  // Convert the positionMap into an array of FormattedItem
  const formattedData: FormattedItem[] = Object.values(positionMap);

  return formattedData;
}

export default function DodavanjeKorisnika() {
  const res =
    useTypedResource<DodavanjeKorisnikaResourcesBSInterface>(
      "dodavanjeKorisnika",
    );

  const usersTemplate = JSON.stringify({
    item: [
      {
        position: "Position1",
        users: [
          {
            email: "ime@safe.ba",
            fullName: "John Doe",
            password: "password123",
          },
        ],
      },
    ],
  });

  // States
  const [branchIdState, setbranchIdState] = useState<string | null>();
  const [foundState, setfoundState] = useState<boolean>(false);
  const [modal, setModal] = useState<boolean>(false);
  const [errorUpdate, setErrorUpdate] = useState<boolean>(false);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [items, setItems] = useState<FormattedItem[] | undefined>();

  // * 2) Check if any Record Point Tree with Branch ID exists
  const {
    loading,
    refetch,
    data,
    error: errorLoading,
  } = useQuery<IBranchUsersQuery>(GET_BRANCH_USERS, {
    variables: {
      branchId: branchIdState ? branchIdState && parseInt(branchIdState) : 0,
    },
    skip: !branchIdState,
  });

  useEffect(() => {
    // * 1) Get Branch ID
    const params = new URLSearchParams(window.location.search);
    const branchId = params.get("branch");
    setbranchIdState(branchId);

    refetch();

    if (!loading && data) {
      if (data.branchUsers && data.branchUsers.length === 0) {
        setfoundState(false);
      } else {
        const jsonData: IFormattedUsersJSON = convertArrayToJSON(
          data.branchUsers ? data.branchUsers : [],
        );

        const formattedItems: FormattedItem[] = formatUsersList(
          jsonData.Positions,
        );

        setItems(formattedItems);
        setfoundState(true);
      }
    }
  }, [loading, data, refetch]);

  // * 3a) If not - show text field - enter json and generate items
  type FormData = {
    users: string;
  };

  const schemaResolver = yupResolver(
    yup.object().shape({
      users: yup.string().required(res.dodavanjeKorisnika.addUsersError),
    }),
  );

  const methods = useForm<ITextArea>({ resolver: schemaResolver });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    setValue,
  } = methods;

  const [createUser] = useMutation(CREATE_USER);

  async function onSubmit(data: FormData) {
    setErrorUpdate(false);
    const encryptionKey = config.encrpytionKey;
    const key = await importKey(encryptionKey);
    if (key) {
      try {
        const parsedJSON: IFormUsers = JSON.parse(data.users);
        for (const userItem of parsedJSON.item) {
          if (userItem.users) {
            for (const user of userItem.users) {
              const encryPassword = await encryptMessage(user.password, key);

              const data: ICreateUserData = {
                branchIds: [Number(branchIdState)],
                fullName: user.fullName,
                email: user.email.toLowerCase(),
                password: encryPassword,
                type: EnumUserType.USER,
                position: userItem.position,
              };
              try {
                await createUser({
                  variables: {
                    data: data,
                  },
                });
                setModal(true);
              } catch (error) {
                setErrorUpdate(true);
                setModal(false);
              }
            }
          }
        }
      } catch (error) {
        setErrorUpdate(true);
        console.error("Invalid JSON Format", error);
      }
    }
    // Add record point trees to database
  }

  function onClickNext() {
    window.location.reload();
    setModal(false);
  }

  // * 3b) If yes - edit existing tree and show results | Update: Load from database - format to json, show as nestable list, after that
  // * go through new json and update each element
  // * Add text area - as input field, that enables adding new elements to the structure - after each add - refresh page
  function updateTree(item: Item, items: Item[]) {
    // TODO: CONVERT TREE TO RECORD POINT TREE AND UPDATE DATABASE - Missing wrong siblings returned
  }

  const RenderItem = ({ item, collapseIcon, handler }: RenderItemProps) => {
    return (
      <div className="dd-list">
        <div className="dd-item">
          <div className="dd-handle">
            {handler}
            {collapseIcon}
            {item.text}
            <FiTrash2
              size={16}
              className="mx-2"
              onClick={() => deleteElements(item.id, item.children)}
            />
          </div>
        </div>
      </div>
    );
  };

  const [deleteRecordPointTree] = useMutation(DELETE_USER);

  function deleteElements(itemId: number, children?: Item[]) {
    setErrorUpdate(false);
    // If the item has children - set all items status to false
    children && deleteItems(children);
    // Set Item status to false and add delete date
    deleteRecordPointTree({
      variables: {
        userId: Number(itemId),
      },
    })
      .then((result) => {
        // Handle success, if needed
        window.location.reload();
      })
      .catch((error) => {
        // Handle error, if needed
        setErrorUpdate(true);
        console.error("Error deleting User:", error.message);
      });
  }

  const deleteItems = async (items: Item[]) => {
    const deleteItem = async (recordPointTreeId: number) => {
      try {
        const result = await deleteRecordPointTree({
          variables: {
            recordPointTreeId: recordPointTreeId,
          },
        });

        if (result.data && result.data.deleteRecordPointTree) {
        } else {
          console.error(
            `Error deleting RecordPointTree with ID ${recordPointTreeId}`,
          );
        }
      } catch (error) {
        console.error("Error deleting RecordPointTree:", error);
      }
    };

    const deleteItemWithChildren = async (item: Item) => {
      if (item.children && item.children.length > 0) {
        // Recursively delete children first
        for (const child of item.children) {
          await deleteItemWithChildren(child);
        }
      }

      // Delete the current item
      await deleteItem(Number(item.id));
    };

    try {
      await Promise.all(items.map(deleteItemWithChildren));
    } catch (error) {
      console.error("Error deleting Users:", error);
    }
  };

  const handleLoadTemplate = () => {
    setValue("users", usersTemplate);
  };

  return (
    <>
      <Row className="mt-2">
        <Col>
          <Card>
            <Card.Body>
              <div
                style={{
                  justifyContent: "center",
                  alignItems: "center",
                  display: "flex",
                }}
              >
                <h1>{res.dodavanjeKorisnika.title}</h1>
              </div>
              <Row>
                {/* Label, Select branch, Button - Add branch */}
                {loading ? (
                  <>
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </Spinner>
                  </>
                ) : (
                  <>
                    <Row className="mb-4">
                      <Col xs="3">
                        <ButtonMain
                          className="rounded-pill waves-effect waves-light"
                          onClick={handleLoadTemplate}
                        >
                          {res.dodavanjeKorisnika.loadTemplateButton}
                        </ButtonMain>
                      </Col>
                    </Row>
                    <Row>
                      <Form onSubmit={handleSubmit(onSubmit)}>
                        <Col xs={12}>
                          <Form.Group className="mb-3">
                            <FormInput
                              label={res.dodavanjeKorisnika.addUsersLabel}
                              type="textarea"
                              name="users"
                              containerClass={"mb-2"}
                              register={register}
                              key="users"
                              errors={errors}
                              control={control}
                            />
                          </Form.Group>
                        </Col>
                        <Col xs={12}>
                          <Button
                            variant="info"
                            className="rounded-pill waves-effect waves-light"
                            type="submit"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? (
                              <>
                                <Spinner
                                  as="span"
                                  animation="border"
                                  size="sm"
                                  role="status"
                                  aria-hidden="true"
                                  className="me-2"
                                />
                                {res.dodavanjeKorisnika.addUsersButton}
                              </>
                            ) : (
                              res.dodavanjeKorisnika.addUsersButton
                            )}
                          </Button>
                        </Col>
                        <Modal
                          show={modal}
                          size="sm"
                          dialogClassName={"modal-dialog-centered"}
                        >
                          <div className={"modal-filled bg-success"}>
                            <Modal.Body className="p-4">
                              <div className="text-center">
                                <i className="dripicons-checkmark h1 text-white"></i>
                                <h4 className="mt-2 text-white">
                                  {res.dodavanjeKorisnika.successModal.title}
                                </h4>
                                <p className="mt-3 text-white">
                                  {res.dodavanjeKorisnika.successModal.body}
                                </p>
                                <button
                                  type="button"
                                  onClick={onClickNext}
                                  className="btn btn-light my-2"
                                >
                                  {res.dodavanjeKorisnika.successModal.buttonOk}
                                </button>
                              </div>
                            </Modal.Body>
                          </div>
                        </Modal>
                      </Form>
                      {errorLoading && (
                        <Alert variant="danger" className="my-4">
                          {res.dodavanjeKorisnika.errorLoading}
                        </Alert>
                      )}
                      {errorUpdate && (
                        <Alert variant="danger" className="my-4">
                          {res.dodavanjeKorisnika.errorUpdate}
                        </Alert>
                      )}
                    </Row>
                    {foundState && (
                      <Row className={"mt-2"}>
                        <hr />
                        <Col lg={12}>
                          <div className="text-start">
                            <Button
                              variant="info"
                              className="rounded-pill waves-effect waves-light mb-2"
                              type="button"
                              onClick={() => setIsCollapsed(!isCollapsed)}
                            >
                              {isCollapsed
                                ? "Prikaži korisnike"
                                : "Prikaži radna mjesta"}
                            </Button>
                          </div>
                        </Col>
                        <Nestable
                          onChange={(e) => updateTree(e.dragItem, e.items)}
                          items={items}
                          renderItem={RenderItem}
                          className="font-13"
                          handler={
                            <span className="dragula-handle text-muted me-3 font-20" />
                          }
                          collapsed={isCollapsed}
                        />
                      </Row>
                    )}
                  </>
                )}
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
}
